import { VITE_API_BASE_URL } from '$lib/env';
import { ClientResponseError } from './ClientResponseError';
import { AdminService } from './services/AdminService';
import { ChatService } from './services/ChatService';
import { ForumService } from './services/ForumService';
import { HackathonService } from './services/HackathonService';
import { ModService } from './services/ModService';
import { NotificationService } from './services/NotificationsService';
import { UserService } from './services/UserService';
import { ProjectService } from './services/ProjectService';
import type { SendOptions } from './services/utils/options';
import { InvestorService } from './services/InvestorService';
import { CompanyService } from './services/CompanyService';
import { ImageService } from './services/ImageService';
import { ReferralService } from './services/ReferralService';
import { AnalyticsService } from './services/AnalyticsService';
import { FeatureService } from './services/FeatureService';
import { AcceleratorService } from './services/AcceleratorService';
import { buildRefreshSessionRedirectUrl } from '$lib/utils/session';
import { API_ERRORS, isKnownErrorResponse } from '$shared/lib';
import { DemoDayService } from './services/DemoDayService';
import { EternalService } from './services/EternalService';
import { WorkshopService } from './services/WorkshopService';
import * as Sentry from '@sentry/sveltekit';

// list of known SendOptions keys (everything else is treated as query param)
const knownSendOptionsKeys = [
  'fetch',
  'headers',
  'body',
  'query',
  'params',
  'cache',
  'credentials',
  'headers',
  'integrity',
  'keepalive',
  'method',
  'mode',
  'redirect',
  'referrer',
  'referrerPolicy',
  'signal',
  'window',
];

export interface BeforeSendResult {
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  [key: string]: any; // for backward compatibility
  url?: string;
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  options?: { [key: string]: any };
}

export default class Client {
  fetchFunc: (
    url: RequestInfo | URL,
    config?: RequestInit,
  ) => Promise<Response>;
  baseUrl: string;
  beforeSend?: (
    url: string,
    options: SendOptions,
  ) => BeforeSendResult | Promise<BeforeSendResult>;

  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  afterSend?: (response: Response, data: any) => any;

  users: UserService;
  projects: ProjectService;
  hackathon: HackathonService;
  eternal: EternalService;
  forums: ForumService;
  chats: ChatService;
  moderation: ModService;
  admin: AdminService;
  notifications: NotificationService;
  investors: InvestorService;
  companies: CompanyService;
  images: ImageService;
  referrals: ReferralService;
  analytics: AnalyticsService;
  features: FeatureService;
  accelerator: AcceleratorService;
  demoDay: DemoDayService;
  workshop: WorkshopService;

  private cancelControllers: {
    [key: string]: {
      controller: AbortController;
      reason?: string;
      url?: string;
    };
  } = {};
  private enableAutoCancellation = true;

  constructor(baseUrl = '/', fetchFunc = fetch) {
    this.fetchFunc = fetchFunc;
    this.baseUrl = baseUrl;
    this.users = new UserService(this);
    this.hackathon = new HackathonService(this);
    this.eternal = new EternalService(this);
    this.chats = new ChatService(this);
    this.forums = new ForumService(this);
    this.moderation = new ModService(this);
    this.admin = new AdminService(this);
    this.notifications = new NotificationService(this);
    this.projects = new ProjectService(this);
    this.investors = new InvestorService(this);
    this.companies = new CompanyService(this);
    this.images = new ImageService(this);
    this.referrals = new ReferralService(this);
    this.analytics = new AnalyticsService(this);
    this.features = new FeatureService(this);
    this.accelerator = new AcceleratorService(this);
    this.demoDay = new DemoDayService(this);
    this.workshop = new WorkshopService(this);
  }
  // Set the fetch function as Sveltekit's event.fetch or window.fetch
  setFetch(fetchFunc: typeof fetch): Client {
    this.fetchFunc = fetchFunc;
    return this;
  }
  /**
   * Globally enable or disable auto cancellation for pending duplicated requests.
   */
  autoCancellation(enable: boolean): Client {
    this.enableAutoCancellation = !!enable;

    return this;
  }

  /**
   * Cancels single request by its cancellation key.
   */
  cancelRequest(
    requestKey: string,
    url: string,
    reason = 'Cancelled due to duplicate request',
  ): Client {
    const entry = this.cancelControllers[requestKey];
    if (entry) {
      // TODO - Reenable abortion functionality
      // entry.controller.abort();
      // console.warn(`Request ${requestKey} to ${url} aborted: ${reason}`);
      delete this.cancelControllers[requestKey];
    }

    return this;
  }

  /**
   * Cancels all pending requests.
   */
  cancelAllRequests(): Client {
    for (const k in this.cancelControllers) {
      this.cancelControllers[k].controller.abort();
    }

    this.cancelControllers = {};

    return this;
  }

  /**
   * Builds a full client url by safely concatenating the provided path.
   */
  buildUrl(path: string): string {
    let url = this.baseUrl;

    // construct an absolute base url if in a browser environment
    if (
      typeof window !== 'undefined' &&
      !!window.location &&
      !url.startsWith('https://') &&
      !url.startsWith('http://')
    ) {
      url = window.location.origin?.endsWith('/')
        ? window.location.origin.substring(0, window.location.origin.length - 1)
        : window.location.origin || '';

      if (!this.baseUrl.startsWith('/')) {
        url += window.location.pathname || '/';
        url += url.endsWith('/') ? '' : '/';
      }

      url += this.baseUrl;
    }

    // concatenate the path
    if (path) {
      url += url.endsWith('/') ? '' : '/'; // append trailing slash if missing
      url += path.startsWith('/') ? path.substring(1) : path;
    }

    return url;
  }

  /**
   * Sends an api http request.
   */
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  async send<T = any>(path: string, options: SendOptions): Promise<T> {
    // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
    options = this.initSendOptions(path, options);

    // build url + path
    let url = this.buildUrl(path);

    if (this.beforeSend) {
      const result = Object.assign({}, await this.beforeSend(url, options));
      if (
        typeof result.url !== 'undefined' ||
        typeof result.options !== 'undefined'
      ) {
        url = result.url || url;
        // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
        options = result.options || options;
      } else if (Object.keys(result).length) {
        // legacy behavior
        // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
        options = result as SendOptions;
        // console?.warn &&
        // 	console.warn(
        // 		'Deprecated format of beforeSend return: please use `return { url, options }`, instead of `return options`.'
        // 	);
      }
    }

    // serialize the query parameters
    if (typeof options.query !== 'undefined') {
      const query = this.serializeQueryParams(options.query);
      if (query) {
        url += (url.includes('?') ? '&' : '?') + query;
      }
      // biome-ignore lint/performance/noDelete: TODO: Don't delete property
      delete options.query;
    }

    // ensures that the json body is serialized
    if (
      this.getHeader(options.headers, 'Content-Type') === 'application/json' &&
      options.body &&
      typeof options.body !== 'string'
    ) {
      options.body = JSON.stringify(options.body);
    }

    options.credentials = 'include';
    // send the request
    return this.fetchFunc(url, options)
      .then(async (response) => {
        // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
        let data: any = {};

        try {
          data = await response.json();
          data = this.convertDates(data);
        } catch (_) {
          // all api responses are expected to return json
          // with the exception of the realtime event and 204
        }

        if (this.afterSend) {
          data = structuredClone(await this.afterSend(response, data));
        }

        if (response.status >= 400) {
          throw new ClientResponseError({
            url: response.url,
            status: response.status,
            response: data,
          });
        }

        return data as T;
      })
      .catch((error) => {
        // Handle known errors
        if (
          error instanceof ClientResponseError &&
          isKnownErrorResponse(error.response)
        ) {
          // If the access token is invalid, attempt to refresh the session
          const shouldRefresh =
            error.status === 401 &&
            error.response.code === API_ERRORS.INVALID_ACCESS_TOKEN;
          if (shouldRefresh) {
            window.location.href = buildRefreshSessionRedirectUrl();
            throw new Error('Session refresh required');
          }

          // Otherwise, throw the error
          throw error;
        }

        // Attempt to capture unknown errors for observability
        try {
          const { signal, ...sanitizedOptions } = options;
          Sentry.captureException(error, {
            extra: { url, options: sanitizedOptions },
          });
        } catch {
          // ignore any logging errors
        }

        throw new ClientResponseError(error);
      });
  }

  /**
   * Shallow copy the provided object and takes care to initialize
   * any options required to preserve the backward compatability.
   *
   * @param  {SendOptions} options
   * @return {SendOptions}
   */
  private initSendOptions(path: string, options: SendOptions): SendOptions {
    // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
    options = Object.assign({ method: 'GET' } as SendOptions, options);
    options.query = options.query || {};

    // auto convert the body to FormData, if needed
    options.body = this.convertToFormDataIfNeeded(options.body);

    // move unknown send options as query parameters
    for (const key in options) {
      if (knownSendOptionsKeys.includes(key)) {
        continue;
      }

      options.query[key] = options[key];
      delete options[key];
    }

    // add the json header, if not explicitly set
    // (for FormData body the Content-Type header should be skipped since the boundary is autogenerated)
    if (
      this.getHeader(options.headers, 'Content-Type') === null &&
      !this.isFormData(options.body)
    ) {
      options.headers = Object.assign({}, options.headers, {
        'Content-Type': 'application/json',
      });
    }

    // handle auto cancelation for duplicated pending request
    if (this.enableAutoCancellation && options.requestKey !== null) {
      const requestKey = options.requestKey || (options.method || 'GET') + path;
      const fullUrl = this.buildUrl(path); // Build full URL to include in the cancellation reason

      // biome-ignore lint/performance/noDelete: TODO: Don't delete property
      delete options.requestKey;

      this.cancelRequest(requestKey, fullUrl, 'Duplicate request');

      const controller = new AbortController();
      this.cancelControllers[requestKey] = {
        controller,
        reason: 'Duplicate request',
        url: fullUrl,
      };
      options.signal = controller.signal;
    }

    return options;
  }

  /**
   * Converts analyzes the provided body and converts it to FormData
   * in case a plain object with File/Blob values is used.
   */
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  private convertToFormDataIfNeeded(body: any): any {
    if (
      typeof FormData === 'undefined' ||
      typeof body === 'undefined' ||
      typeof body !== 'object' ||
      body === null ||
      this.isFormData(body) ||
      !this.hasBlobField(body)
    ) {
      return body;
    }

    const form = new FormData();

    for (const key in body) {
      const values = Array.isArray(body[key]) ? body[key] : [body[key]];
      for (const val of values) {
        form.append(key, val);
      }
    }

    return form;
  }

  /**
   * Checks if the submitted body object has at least one Blob/File field.
   */
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  private hasBlobField(body: { [key: string]: any }): boolean {
    for (const key in body) {
      const values = Array.isArray(body[key]) ? body[key] : [body[key]];
      for (const v of values) {
        if (
          (typeof Blob !== 'undefined' && v instanceof Blob) ||
          (typeof File !== 'undefined' && v instanceof File)
        ) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Extracts the header with the provided name in case-insensitive manner.
   * Returns `null` if no header matching the name is found.
   */
  private getHeader(
    headers: { [key: string]: string } | undefined,
    name: string,
  ): string | null {
    // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
    headers = headers || {};
    // biome-ignore lint/style/noParameterAssign: TODO: Don't reassign parameter
    name = name.toLowerCase();

    for (const key in headers) {
      if (key.toLowerCase() === name) {
        return headers[key];
      }
    }

    return null;
  }

  /**
   * Loosely checks if the specified body is a FormData instance.
   */
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  private isFormData(body: any): boolean {
    return (
      body &&
      // we are checking the constructor name because FormData
      // is not available natively in some environments and the
      // polyfill(s) may not be globally accessible
      (body.constructor.name === 'FormData' ||
        // fallback to global FormData instance check
        // note: this is needed because the constructor.name could be different in case of
        //       custom global FormData implementation, eg. React Native on Android/iOS
        (typeof FormData !== 'undefined' && body instanceof FormData))
    );
  }

  /**
   * Serializes the provided query parameters into a query string.
   */
  // biome-ignore lint/suspicious/noExplicitAny: TODO: Fix this missing type
  private serializeQueryParams(params: { [key: string]: any }): string {
    const result: Array<string> = [];
    for (const key in params) {
      if (params[key] === null || params[key] === undefined) {
        // skip null and undefined query params
        continue;
      }

      const value = params[key];
      const encodedKey = encodeURIComponent(key);

      if (Array.isArray(value)) {
        // repeat array params
        for (const v of value) {
          if (v !== undefined) {
            result.push(`${encodedKey}[]=${encodeURIComponent(v)}`);
          }
        }
      } else if (value instanceof Date) {
        result.push(`${encodedKey}=${encodeURIComponent(value.toISOString())}`);
      } else if (value !== null && typeof value === 'object') {
        result.push(
          `${encodedKey}=${encodeURIComponent(JSON.stringify(value))}`,
        );
      } else {
        result.push(`${encodedKey}=${encodeURIComponent(value)}`);
      }
    }

    return result.join('&');
  }

  /**
   * Checks if a string matches the ISO 8601 date format.
   * @param {string} str - The string to check.
   * @returns {boolean} - True if the string is an ISO 8601 date, false otherwise.
   */
  private isDateString(str: string): boolean {
    const iso8601Regex =
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;
    return iso8601Regex.test(str);
  }

  /**
   * Recursively converts ISO 8601 date strings to Date objects in the provided data.
   * @param {unknown} data - The data to process (object, array, or primitive).
   * @returns {unknown} - The data with date strings converted to Date objects.
   */
  private convertDates(data: unknown): unknown {
    // Convert dates in strings
    if (typeof data === 'string') {
      if (this.isDateString(data)) {
        const date = new Date(data);
        if (!Number.isNaN(date.getTime())) {
          return date;
        }
      }
      return data;
    }

    // Recursively convert dates in arrays
    if (Array.isArray(data)) {
      return data.map((item) => this.convertDates(item));
    }

    // Recursively convert dates in objects
    if (typeof data === 'object' && data !== null) {
      const obj = data as { [key: string]: unknown };
      const result: { [key: string]: unknown } = {};
      for (const key in obj) {
        result[key] = this.convertDates(obj[key]);
      }
      return result;
    }

    return data;
  }
}

export const client = new Client(VITE_API_BASE_URL as string);
